import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { GaleriaTrabajosComponent } from '../galeria-trabajos/galeria-trabajos.component';
import { TrabajosmenuComponent } from '../trabajosmenu/trabajosmenu.component';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { Metodosui } from '../../services/metodosui';

@Component({
  selector: 'app-dashboard-records-modal',
  templateUrl: './dashboard-records-modal.component.html',
  styleUrls: ['./dashboard-records-modal.component.scss'],
  standalone: false
})
export class DashboardRecordsModalComponent implements OnInit {

  @Input() titulo: string = 'Registros Operativos';
  @Input() subtitulo: string = '';
  @Input() icono: string = 'list-outline';
  @Input() color: string = 'blue';
  @Input() records: any[] = [];
  @Input() accionResolucion: string = 'galeria'; // fallback retrocompatible
  @Input() dataSourceId: string = 'ds_trabajos';
  @Input() camposDetalle: any[] = [];
  @Input() accionesModal: any[] = [];

  searchText: string = '';
  enabledArsClients: string[] = ['inmobiliaria don antonio'];
  isAdminUser: boolean = false;

  // Modo de vista: tarjeta baraja como vista inicial predeterminada
  viewMode: 'list' | 'card' = 'card';
  currentCardIndex: number = 0;
  cardAnimation: 'left' | 'right' | '' = '';
  private touchStartX: number = 0;

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private router: Router
  ) { }

  async ngOnInit() {
    this.detectarRolAdmin();
    await this.cargarParametroArsClients();
  }

  private detectarRolAdmin() {
    try {
      if (this.metodosGenService.isSuperAdmin()) {
        this.isAdminUser = true;
        return;
      }
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const rol = (user.l_rol_nombre || '').toLowerCase();
        if (rol.includes('admin') || rol.includes('geren') || rol.includes('super')) {
          this.isAdminUser = true;
          return;
        }
      }
      const perms = this.metodosGenService.getPermissions();
      if (perms?.ajustes?.rl_adm_users === true || perms?.ajustes?.rl_adm_roles === true) {
        this.isAdminUser = true;
      }
    } catch (e) {
      this.isAdminUser = false;
    }
  }

  setViewMode(mode: 'list' | 'card') {
    this.viewMode = mode;
    if (this.currentCardIndex >= this.filteredRecords.length) {
      this.currentCardIndex = 0;
    }
  }

  get currentCard(): any | null {
    const list = this.filteredRecords;
    if (list && list.length > 0) {
      const idx = Math.min(Math.max(0, this.currentCardIndex), list.length - 1);
      return list[idx];
    }
    return null;
  }

  get stackedCardTabs(): { card: any; originalIndex: number }[] {
    const list = this.filteredRecords;
    if (!list || list.length <= 1) return [];
    const tabs: { card: any; originalIndex: number }[] = [];
    const maxTabs = Math.min(2, list.length - 1);
    for (let i = 1; i <= maxTabs; i++) {
      const targetIdx = (this.currentCardIndex + i) % list.length;
      tabs.push({ card: list[targetIdx], originalIndex: targetIdx });
    }
    return tabs.reverse();
  }

  prevCard() {
    const list = this.filteredRecords;
    if (list.length <= 1) return;
    this.currentCardIndex = (this.currentCardIndex - 1 + list.length) % list.length;
  }

  nextCard() {
    const list = this.filteredRecords;
    if (list.length <= 1) return;
    this.currentCardIndex = (this.currentCardIndex + 1) % list.length;
  }

  selectCard(index: number) {
    if (index >= 0 && index < this.filteredRecords.length) {
      this.currentCardIndex = index;
    }
  }

  onCardTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
  }

  get chipTiempoActivo(): any | null {
    if (!Array.isArray(this.camposDetalle)) return null;
    return this.camposDetalle.find(c => {
      const field = (typeof c === 'string' ? c : (c.campo || c.id || '')).toLowerCase();
      return field === 'dias_activo' || field === 'dias' || field === 'dias_proceso';
    }) || null;
  }

  get camposDetalleCard(): any[] {
    if (!Array.isArray(this.camposDetalle)) return [];
    // Filtrar 'lugar' (tiene bloque Hero) y 'tiempo activo' si se muestra en su propia fila destacada
    return this.camposDetalle.filter(c => {
      const field = (typeof c === 'string' ? c : (c.campo || c.id || '')).toLowerCase();
      return field !== 'lugar' && field !== 'ubicacion' && field !== 'dias_activo' && field !== 'dias' && field !== 'dias_proceso';
    });
  }

  onCardTouchEnd(event: TouchEvent) {
    const touchEndX = event.changedTouches[0].clientX;
    const diffX = touchEndX - this.touchStartX;
    if (Math.abs(diffX) > 60) {
      if (diffX > 0) {
        this.prevCard();
      } else {
        this.nextCard();
      }
    }
  }

  private async cargarParametroArsClients() {
    try {
      const res: any = await firstValueFrom(this.javapiService.getJsonParam('enable_ars_action')).catch(() => null);
      if (res && res.pa_json) {
        const parsed = JSON.parse(res.pa_json);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.enabledArsClients = parsed.map((c: string) => (c || '').trim().toLowerCase());
        }
      }
    } catch (e) {
      console.warn('Error cargando enable_ars_action en modal de registros:', e);
    }
  }

  isArsClient(record: any): boolean {
    const cliente = (record?.cliente || record?.dt2 || '').trim().toLowerCase();
    return this.enabledArsClients.some(c => cliente.includes(c));
  }

  get filteredRecords(): any[] {
    if (!this.searchText || !this.searchText.trim()) {
      return this.records;
    }
    const q = this.searchText.trim().toLowerCase();
    return this.records.filter(r => {
      const id = String(r.id || '').toLowerCase();
      const cli = String(r.cliente || '').toLowerCase();
      const lug = String(r.lugar || '').toLowerCase();
      const sol = String(r.solicitante || '').toLowerCase();
      const des = String(r.descripcion || r.dt8 || '').toLowerCase();
      return id.includes(q) || cli.includes(q) || lug.includes(q) || sol.includes(q) || des.includes(q);
    });
  }

  /**
   * Obtiene el valor formateado de un campo de detalle configurado para el registro
   */
  getValorCampo(item: any, campoCfg: any): string {
    if (!item || !campoCfg) return '';
    const prop = campoCfg.campo;

    // Campos compuestos específicos
    if (prop === 'ultimo_registro_planilla' || prop === 'ultima_fecha_planilla') {
      const fecha = item.ultima_fecha_planilla;
      if (!fecha) return 'Sin registros de planilla';
      return fecha;
    }

    if (prop === 'dias_sin_planilla') {
      const pCount = Number(item.planilla_count ?? 0);
      const dSinPlanilla = Number(item.dias_sin_planilla);
      if (pCount === 0 || dSinPlanilla >= 999 || isNaN(dSinPlanilla)) {
        return 'Sin planilla registrada';
      }
      const suffix = campoCfg.sufijo ? ` ${campoCfg.sufijo}` : ' días';
      return `${dSinPlanilla}${suffix}`.trim();
    }

    if (prop === 'fecha_envio_cot') {
      return item.fecha_envio_cot ? item.fecha_envio_cot : 'No enviada por correo';
    }

    if (prop === 'fecha_envio_ars' || prop === 'envio_o_carga_ars') {
      if (item.fecha_envio_ars) {
        return `Enviado el ${item.fecha_envio_ars}`;
      }
      if (item.tiene_ars_pdf) {
        return 'No enviado (Tiene PDF cargado)';
      }
      if (item.firmado || item.ars_firmado_db) {
        return 'No enviado (Reporte ya firmado)';
      }
      return 'No enviado / Sin ARS cargado';
    }

    let val = item[prop];
    if (val === undefined || val === null || val === '') {
      if (prop === 'fecha' && item.dt4) val = item.dt4;
      if (prop === 'dias' && (item.dt13 || item.dt10)) val = item.dt13 || item.dt10;
      if (prop === 'fotos' && (item.dt14 || item.dt11)) val = item.dt14 || item.dt11;
      if (prop === 'lugar' && item.dt3) val = item.dt3;
      if (prop === 'solicitante' && item.dt5) val = item.dt5;
    }
    if (val === undefined || val === null || val === '') return '';
    const prefix = campoCfg.prefijo ? `${campoCfg.prefijo} ` : '';
    const suffix = campoCfg.sufijo ? ` ${campoCfg.sufijo}` : '';
    return `${prefix}${val}${suffix}`.trim();
  }

  /**
   * Evalúa si una acción de botón configurada aplica para un registro
   */
  debeMostrarAccion(accionCfg: any, record: any): boolean {
    const isArs = this.isArsClient(record);
    const firmado = (record.firmado === true || record.firmado === 'Y' || record.dt9 === 'Y');

    // Regla de Negocio para Finalizar:
    // 1. Si requiere ARS, el usuario regular NO lo ve; solo Admin hacia arriba y cuando ya esté firmado.
    // 2. Si no requiere ARS, cualquier rol puede finalizarlo.
    if (accionCfg.tipo === 'accion_directa' && accionCfg.handler === 'finalizar') {
      if (isArs) {
        return this.isAdminUser && firmado;
      }
      return true;
    }

    // Regla de Negocio para Marcar ARS Firmado:
    // Solo aplica para clientes con ARS y cuando aún no ha sido firmado.
    if (accionCfg.tipo === 'accion_directa' && accionCfg.handler === 'marcar_ars') {
      return isArs && !firmado;
    }

    if (!accionCfg.condicion) return true;
    const cond = accionCfg.condicion;

    if (cond === 'isArsClient && !firmado') {
      return isArs && !firmado;
    }
    if (cond === 'isArsClient && firmado') {
      return isArs && firmado;
    }
    if (cond === '!isArsClient') {
      return !isArs;
    }
    if (cond === 'isArsClient') {
      return isArs;
    }
    if (cond === '!firmado') {
      return !firmado;
    }
    return true;
  }

  /**
   * Ejecuta una acción de botón configurada (modal, ruta o handler directo)
   */
  async ejecutarBotonConfigurado(accionCfg: any, record: any, event?: Event) {
    if (event) event.stopPropagation();

    if (accionCfg.tipo === 'accion_directa') {
      if (accionCfg.handler === 'marcar_ars') {
        await this.confirmarMarcarArs(record, event);
      } else if (accionCfg.handler === 'finalizar') {
        await this.confirmarFinalizarTrabajo(record, event);
      }
      return;
    }

    if (accionCfg.tipo === 'ruta' && accionCfg.ruta) {
      await this.modalCtrl.dismiss();
      const queryParams: any = {};
      if (accionCfg.query) {
        Object.assign(queryParams, accionCfg.query);
      }
      if (record.id || record.dt1) {
        queryParams['id'] = record.id || record.dt1;
      }
      this.router.navigate([accionCfg.ruta], { queryParams });
      return;
    }

    // Por defecto es modal
    const componente = accionCfg.componente || accionCfg.accion || this.accionResolucion;
    await this.abrirComponenteModal(componente, record);
  }

  async confirmarMarcarArs(record: any, event?: Event) {
    if (event) event.stopPropagation();

    const idTrabajo = record.id || record.dt1;
    const lugar = record.lugar || record.dt3 || 'Trabajo';

    const confirmado = await this.metodosUi.showConfirmationPgV2({
      header: 'Confirmar Firma de Reporte',
      subHeader: `Trabajo #${idTrabajo} - ${lugar}`,
      message: `¿Confirmas que ya se firmó el reporte ARS para el cliente ${record.cliente || record.dt2}?`,
      confirmText: 'Sí, marcar firmado',
      cancelText: 'Cancelar'
    });

    if (!confirmado) return;

    try {
      let usuario = 'system';
      const userJson = localStorage.getItem('user');
      if (userJson) {
        usuario = JSON.parse(userJson).l_usuario || 'system';
      }

      const payload = this.metodosGenService.setModeloGeneral(
        'genUpdate', 'updateArsStatus',
        String(idTrabajo),
        'Y',
        '',
        usuario,
        '', '', '', '', '', '', '', '', '', '', ''
      );

      const res = await firstValueFrom(this.javapiService.getCommonModel(payload));
      if (res) {
        record.firmado = true;
        record.dt9 = 'Y';
        this.records = this.records.filter(r => (r.id || r.dt1) !== idTrabajo);
        this.metodosUi.showToast('Reporte ARS marcado como firmado con éxito', 'success', 'bottom', 2000);
      }
    } catch (e: any) {
      console.error('Error marcando reporte ARS:', e);
      this.metodosUi.showToast('Error al marcar reporte: ' + (e.message || e), 'danger', 'bottom');
    }
  }

  async confirmarFinalizarTrabajo(record: any, event?: Event) {
    if (event) event.stopPropagation();

    const idTrabajo = record.id || record.dt1;
    const lugar = record.lugar || record.dt3 || 'Trabajo';

    try {
      const payloadTotals = this.metodosGenService.setModeloGeneral(
        'genModel', 'menuTotals',
        String(idTrabajo),
        '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      );
      const resTotals: any = await firstValueFrom(this.javapiService.getCommonModel(payloadTotals)).catch(() => []);

      if (resTotals && resTotals.length > 0) {
        const diasTrabajados = Number(resTotals[0].dt3) || 0;
        const galleryCount = Number(resTotals[0].dt6) || 0;

        if (diasTrabajados <= 0) {
          await this.metodosUi.showAlert('No se puede finalizar', 'El trabajo no tiene planilla registrada. Debe tener al menos un día de trabajo.');
          return;
        }
        if (galleryCount <= 0) {
          await this.metodosUi.showAlert('No se puede finalizar', 'El trabajo no tiene imágenes en la galería. Debe subir evidencia fotográfica.');
          return;
        }

        const cliente = (record.cliente || record.dt2 || '').toLowerCase();
        const estaFirmado = (record.firmado === true || record.firmado === 'Y' || record.dt9 === 'Y' || record.ars_firmado_db);
        if (this.isArsClient(record) && !estaFirmado) {
          await this.metodosUi.showAlert(
            'Falta reporte ARS',
            `Para el cliente ${record.cliente || 'Inmobiliaria Don Antonio'} es obligatorio adjuntar el reporte ARS firmado antes de finalizar el trabajo.`
          );
          return;
        }
      }
    } catch (errVal) {
      console.warn('Advertencia validando totales de finalización:', errVal);
    }

    const confirmado = await this.metodosUi.showConfirmationPgV2({
      header: 'Finalizar Trabajo',
      subHeader: `Trabajo #${idTrabajo} - ${lugar}`,
      message: `¿Estás seguro de marcar este trabajo como Finalizado?`,
      confirmText: 'Finalizar trabajo',
      cancelText: 'Cancelar'
    });

    if (!confirmado) return;

    try {
      let usuario = 'system';
      const userJson = localStorage.getItem('user');
      if (userJson) {
        usuario = JSON.parse(userJson).l_usuario || 'system';
      }

      const payload = this.metodosGenService.setModeloGeneral(
        'genUpdate', 'trabajoStatusUpdate',
        String(idTrabajo),
        'Finalizado',
        'Finalizado desde Atención Operativa',
        usuario,
        '', '', '', '', '', '', '', '', '', '', ''
      );

      const res = await firstValueFrom(this.javapiService.getCommonModel(payload));
      if (res) {
        record.estado = 'Finalizado';
        record.dt7 = 'Finalizado';
        this.records = this.records.filter(r => (r.id || r.dt1) !== idTrabajo);
        this.metodosUi.showToast('Trabajo marcado como Finalizado con éxito', 'success', 'bottom', 2000);
      }
    } catch (e: any) {
      console.error('Error finalizando trabajo:', e);
      this.metodosUi.showToast('Error al finalizar trabajo: ' + (e.message || e), 'danger', 'bottom');
    }
  }

  async ejecutarAccion(record: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    // Al hacer clic sobre el cuerpo de la tarjeta/item, solo abrir modal de detalle/información (nunca una acción_directa como finalizar)
    if (this.accionesModal && this.accionesModal.length > 0) {
      const accionModalInformativa = this.accionesModal.find((a: any) => a.tipo === 'modal' || (a.tipo !== 'accion_directa' && !a.handler));
      if (accionModalInformativa) {
        await this.ejecutarBotonConfigurado(accionModalInformativa, record, event);
        return;
      }
    }
    const accion = this.accionResolucion === 'galeria' ? 'galeria' : 'trabajo_menu';
    await this.abrirComponenteModal(accion, record);
  }

  private async abrirComponenteModal(accion: string, record: any) {
    const trabajoObj = {
      id: Number(record.id || record.dt1) || 0,
      cliente: record.cliente || record.dt2 || '',
      lugar: record.lugar || record.dt3 || '',
      solicitante: record.solicitante || record.dt5 || '',
      descripcion: record.descripcion || record.dt8 || record.dt4 || '',
      incluye_material: record.incluye_material || record.dt6 || 'Y',
      estado: record.estado || record.dt7 || 'Pendiente',
      fecha_creacion: record.fecha || record.fecha_creacion || record.dt4 || new Date().toISOString(),
      isArsSigned: record.firmado === true || record.firmado === 'Y' || record.dt9 === 'Y' || record.ars_firmado_db === true,
      hasArsAttachment: record.tiene_ars_pdf === true,
      diasTrabajados: Number(record.dias || record.dt13 || record.dt10 || 0),
      galleryCount: Number(record.fotos || record.dt14 || record.dt11 || 0)
    };

    if (accion === 'galeria') {
      const modal = await this.modalCtrl.create({
        component: GaleriaTrabajosComponent,
        componentProps: {
          trabajo: trabajoObj
        }
      });
      await modal.present();
      await modal.onWillDismiss();
    } else {
      const modal = await this.modalCtrl.create({
        component: TrabajosmenuComponent,
        componentProps: {
          trabajo: trabajoObj
        }
      });
      await modal.present();
      await modal.onWillDismiss();
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
